import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {Box, Chip, Typography} from '@mui/material';

import {greenColors, orangeColors, neutral} from '../../../../../layouts/theme/designSystem';
import type {TargetProfileSkill} from '../../types/DepartmentTargetProfileTypes';

const EMPTY_SKILL_ID_SET: ReadonlySet<number> = new Set();

export interface Props {
  label: string;
  skills: TargetProfileSkill[];
  language: keyof TargetProfileSkill['name'];
  variant: 'mustHave' | 'niceToHave';
  excludedSkillIds?: ReadonlySet<number>;
  onToggleSkillExclusion?: (standardSkillId: number) => void;
  onRemoveSkill?: (standardSkillId: number) => void;
  onToggleRequirementLevel?: (skill: TargetProfileSkill) => void;
  excludeSkillLabel?: string;
  includeSkillLabel?: string;
  removeSkillLabel?: string;
  toggleRequirementLabel?: string;
  showLabelWhenEmpty?: boolean;
}

export const TargetProfileSkillChipGroup = ({
  label,
  skills,
  language,
  variant,
  excludedSkillIds = EMPTY_SKILL_ID_SET,
  onToggleSkillExclusion,
  onRemoveSkill,
  onToggleRequirementLevel,
  excludeSkillLabel,
  includeSkillLabel,
  removeSkillLabel,
  toggleRequirementLabel,
  showLabelWhenEmpty = false,
}: Props) => {
  const isExclusionEditable = Boolean(onToggleSkillExclusion);
  const isCurationEditable = Boolean(onRemoveSkill);
  if (!skills.length && !showLabelWhenEmpty) {
    return null;
  }

  const activeChipSx =
    variant === 'mustHave'
      ? {bgcolor: greenColors[5], border: `1px solid ${greenColors[3]}`}
      : {bgcolor: orangeColors[5], border: `1px solid ${orangeColors[3]}`};

  return (
    <Box sx={{mb: 1}}>
      <Typography variant='body2' sx={{color: greenColors[700], fontWeight: 600, mb: 0.5}}>
        {label}
      </Typography>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75}}>
        {skills.map((skill) => {
          const isExcluded = excludedSkillIds.has(skill.standardSkillId);

          return (
            <Chip
              key={`${skill.standardSkillId}-${skill.sortOrder}`}
              label={skill.name[language]}
              size='small'
              onClick={
                isCurationEditable && onToggleRequirementLevel
                  ? () => onToggleRequirementLevel(skill)
                  : undefined
              }
              onDelete={
                isExclusionEditable
                  ? () => onToggleSkillExclusion?.(skill.standardSkillId)
                  : isCurationEditable
                    ? () => onRemoveSkill?.(skill.standardSkillId)
                    : undefined
              }
              deleteIcon={
                isExclusionEditable ? (
                  isExcluded ? (
                    <AddIcon fontSize='small' aria-label={includeSkillLabel} />
                  ) : (
                    <CloseIcon fontSize='small' aria-label={excludeSkillLabel} />
                  )
                ) : isCurationEditable ? (
                  <CloseIcon fontSize='small' aria-label={removeSkillLabel} />
                ) : undefined
              }
              title={isCurationEditable ? toggleRequirementLabel : undefined}
              sx={{
                ...(isExcluded
                  ? {
                    bgcolor: neutral[100],
                    border: `1px solid ${neutral[300]}`,
                    '& .MuiChip-label': {
                      textDecoration: 'line-through',
                      color: neutral[600],
                    },
                  }
                  : activeChipSx),
                ...(isCurationEditable && onToggleRequirementLevel ? {cursor: 'pointer'} : {}),
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};
